import { syntaxTree } from "@codemirror/language";
import { EditorSelection, type StateCommand } from "@codemirror/state";
import { type SyntaxNode } from "@lezer/common";

/** Enter-key behaviours that make the writing surface feel like a word
 *  processor rather than a text field. Both run BEFORE markdown's
 *  continue-list Enter (see the keymap in `CodeMirrorEditor.tsx`) and both
 *  return false when they do not apply, so list/quote continuation is
 *  untouched everywhere else. */

/** Inline emphasis that should carry onto the next line, mapped to the marker
 *  that opens and closes it. Deliberately NOT headings, blockquotes or list
 *  markers: those are block-level, they mean something different on a new line,
 *  and markdown's own continue-list command already handles them. */
const CARRIED: Record<string, string> = {
  StrongEmphasis: "**",
  Emphasis: "*",
  Strikethrough: "~~",
  Highlight: "==",
};

/** Characters that can form a horizontal rule, and the run length one needs. */
const RULE_CHARS = new Set(["-", "_", "*"]);
const RULE_MIN = 3;

/** The innermost carried-emphasis node the caret sits INSIDE — inside meaning
 *  within the content, not out past the closing marker. Returns the node and
 *  its marker, or null. */
function emphasisAt(state: Parameters<StateCommand>[0]["state"], pos: number) {
  for (let n: SyntaxNode | null = syntaxTree(state).resolveInner(pos, -1); n; n = n.parent) {
    const marker = CARRIED[n.name];
    if (!marker) continue;
    // The caret must be strictly between the markers. At `**bold**|` the caret
    // has left the span and Enter is just a newline.
    if (pos <= n.from + marker.length || pos > n.to - marker.length) return null;
    return { node: n, marker };
  }
  return null;
}

/** Symmetric markers an Enter can clear, LONGEST FIRST — `**|**` has to be seen
 *  as one `**` pair, not as a `*` pair wrapping another, or clearing it would
 *  leave a stray asterisk on each line. */
const PAIRS = ["***", "**", "__", "==", "~~", "*", "_", "`"];

/** Enter inside an EMPTY marker pair throws the markers away instead of
 *  carrying them onto the next line.
 *
 *  This is the other half of `continueEmphasisOnEnter`, and it is what makes
 *  pressing Enter twice mean "I am done with this". The first Enter closes the
 *  run and reopens it below (`**bold**` / `**|**`); if you had no more bold to
 *  write, the second lands in that empty pair — and without this it would
 *  cheerfully continue it again, leaving `****` stranded on a line. Exactly the
 *  behaviour markdown's own continue-list has for an empty `- ` item. */
export const clearEmptyPairOnEnter: StateCommand = ({ state, dispatch }) => {
  const range = state.selection.main;
  if (!range.empty) return false;
  for (const marker of PAIRS) {
    const before = state.sliceDoc(Math.max(0, range.from - marker.length), range.from);
    const after = state.sliceDoc(range.to, Math.min(state.doc.length, range.to + marker.length));
    if (before !== marker || after !== marker) continue;
    const from = range.from - marker.length;
    dispatch(
      state.update({
        changes: { from, to: range.to + marker.length, insert: "\n" },
        selection: EditorSelection.cursor(from + 1),
        scrollIntoView: true,
        userEvent: "input",
      }),
    );
    return true;
  }
  return false;
};

/** A line holding nothing but marker characters, e.g. a `**` left behind after
 *  the text around it was deleted. Rules (`---`, `___`) are excluded — those are
 *  real content and `completeRuleOnEnter` owns them. */
const ORPHAN_MARKERS = /^[ \t]*(?:\*{1,2}|_{1,2}|={2}|~{2}|`)[ \t]*$/;

/** Enter on a line that is only leftover markers clears the line rather than
 *  pushing the debris down the document. Typing `**`, changing your mind, and
 *  pressing Enter should not leave a line that renders as two literal asterisks
 *  forever. */
export const clearOrphanMarkersOnEnter: StateCommand = ({ state, dispatch }) => {
  const range = state.selection.main;
  if (!range.empty) return false;
  const line = state.doc.lineAt(range.from);
  if (!ORPHAN_MARKERS.test(line.text) || !line.text.trim()) return false;
  // A rule is three or more of the same character — never touch one.
  if (/^[ \t]*([-_*])\1{2,}[ \t]*$/.test(line.text)) return false;
  dispatch(
    state.update({
      changes: { from: line.from, to: line.to, insert: "\n" },
      selection: EditorSelection.cursor(line.from + 1),
      scrollIntoView: true,
      userEvent: "input",
    }),
  );
  return true;
};

/** Google-Docs-style emphasis continuation: pressing Enter while writing in
 *  **bold** (or *italic*, ~~struck~~, ==highlight==) closes the run on this
 *  line and REOPENS it on the next, so the next line keeps the effect instead
 *  of the closing marker simply sliding down with the caret.
 *
 *  `**bold|**` + Enter becomes `**bold**` / `****` with the caret between the
 *  new line's markers — the already-present closer becomes that line's closer,
 *  so this only ever inserts `marker + newline + marker`. */
export const continueEmphasisOnEnter: StateCommand = ({ state, dispatch }) => {
  const range = state.selection.main;
  if (!range.empty) return false;
  const hit = emphasisAt(state, range.from);
  if (!hit) return false;
  const insert = `${hit.marker}\n${hit.marker}`;
  dispatch(
    state.update({
      changes: { from: range.from, insert },
      // Caret lands between the reopened marker and the original closer.
      selection: EditorSelection.cursor(range.from + insert.length),
      scrollIntoView: true,
      userEvent: "input",
    }),
  );
  return true;
};

/** Pressing Enter part-way through a run of rule characters finishes the rule
 *  and throws the leftovers away.
 *
 *  Auto-pairing means typing `___` actually lands `___|___` in the document —
 *  every marker brought its own partner. Enter used to split that down the
 *  middle and drag `___` onto the next line, where it silently glued itself to
 *  whatever was typed next (`___` / `after___`). Now the line is truncated to
 *  the rule the user clearly meant and the caret starts a clean line. */
export const completeRuleOnEnter: StateCommand = ({ state, dispatch }) => {
  const range = state.selection.main;
  if (!range.empty) return false;
  const line = state.doc.lineAt(range.from);
  const text = line.text;
  const ch = text[0];
  if (!ch || !RULE_CHARS.has(ch)) return false;
  // The whole line must be one run of the same rule character — a line like
  // `- item` or `*bold*` is not a rule and must fall through to continue-list.
  for (const c of text) if (c !== ch) return false;
  if (text.length < RULE_MIN) return false;
  // Nothing to clean up when the caret is already at the end of a bare rule.
  if (range.from === line.to) return false;
  // A line of `*` or `_` is ambiguous: `****` is a rule by the letter of the
  // rule above, but it is what typing `**` actually leaves in the document once
  // auto-pairing has added the closer — and truncating it to `***` (a rule!) is
  // the opposite of what someone starting a bold run wants. An EMPTY PAIR is
  // symmetric about the caret with at most two markers a side; a rule being
  // typed has three or more (`***` auto-pairs to `***|***`). Hand the pair case
  // to `clearEmptyPairOnEnter`.
  const leftRun = range.from - line.from;
  const rightRun = line.to - range.from;
  if (leftRun === rightRun && leftRun <= 2) return false;
  const kept = Math.max(RULE_MIN, range.from - line.from);
  dispatch(
    state.update({
      changes: { from: line.from + kept, to: line.to, insert: "\n" },
      selection: EditorSelection.cursor(line.from + kept + 1),
      scrollIntoView: true,
      userEvent: "input",
    }),
  );
  return true;
};
